import type {
  BillingPrime,
  MissionPrime,
  MonthlyTamPrime,
  MonthlyBloomieMargin,
  BasePrime,
  IBloomies,
  TamSeniority,
  PrimeRegularisation,
} from '@bloomays-lib/types.shared';
import type { SalesTadOption } from '../constants/salesTadMargins';

/** Champs affichés dans le DataGridV2 (TEC-2805). */
type TamPrimeTableField =
  | 'jobFunnelProjectId'
  | 'airtableId'
  | 'errorCode'
  | 'carryInSourceMonthYear'
  | 'clientSocietyName'
  | 'bloomerFullName'
  | 'jobType'
  | 'monthlyMargin'
  | 'monthlyPrime'
  | 'splitRate'
  | 'commissionRate'
  | 'split';

type MissionPrimeCraField =
  | 'craStatus'
  | 'normalWorkedDays'
  | 'saturdaysWorkedDays'
  | 'sundayAndholidaysWorkedDays'
  | 'astreinteWorkedDays'
  | 'craAirtableId';

export type MissionPrimeLine = Pick<MissionPrime, TamPrimeTableField | MissionPrimeCraField> & {
  __typename?: 'MissionPrime';
};

export type BillingPrimeLine = Pick<BillingPrime, TamPrimeTableField | 'paymentTerms'> & {
  __typename?: 'BillingPrime';
};

export type TamPrimeLine = MissionPrimeLine | BillingPrimeLine;

type MonthlyTamPrimeAggregateField =
  | 'bonus'
  | 'regularisation'
  | 'regularisationReason'
  | 'totalFreelanceMarge'
  | 'totalBillingMarge'
  | 'totalMarge'
  | 'totalPrime'
  | 'totalDeals'
  | 'totalMissions';

type MonthlyTamPrimeAggregateBase = Pick<MonthlyTamPrime, MonthlyTamPrimeAggregateField> & {
  primes: TamPrimeLine[];
};

/** Réponse pending `getMonthlyTamPrime`. */
export type MonthlyTamPrimeAggregate = MonthlyTamPrimeAggregateBase & {
  __typename?: 'MonthlyTamPrime';
};

/** Réponse validée `getMonthlyTamPrime`. */
export type ValidatedTamPrimeAggregate = MonthlyTamPrimeAggregateBase & {
  __typename: 'ValidatedTamPrime';
  id: string;
  monthYear: string;
  validationDate: string;
};

export type MonthlyTamPrimeQueryResult = MonthlyTamPrimeAggregate | ValidatedTamPrimeAggregate;

export type GetMonthlyTamPrimeQueryData = {
  getMonthlyTamPrime: MonthlyTamPrimeQueryResult;
};

type PrimeRegularisationQueryField = 'id' | 'tam' | 'monthYear' | 'total' | 'reason' | 'status';

export type PrimeRegularisationQueryRow = Pick<PrimeRegularisation, PrimeRegularisationQueryField>;

export type GetPrimeRegularisationByTamAndMonthQueryData = {
  getPrimeRegularisationByTamAndMonth: PrimeRegularisationQueryRow | null;
};

export type MonthlyPrimeHeaderAmounts = {
  freelanceMarge: number;
  billingMarge: number;
  commission: number;
  bonus: number;
  regularisation: number;
  regularisationReason?: string | null;
  total: number;
};

export type MissionPrimeAdditionalDataInput = Pick<
  MissionPrime,
  'normalWorkedDays' | 'saturdaysWorkedDays' | 'sundayAndholidaysWorkedDays' | 'astreinteWorkedDays'
>;

type PrimeLineValidationInputBase = Omit<
  BasePrime,
  'clientSocietyName' | 'bloomerFullName' | 'jobType' | 'airtableId' | 'errorCode' | 'split'
>;

export type MissionMonthlyPrimeLineValidationInput = PrimeLineValidationInputBase & {
  additionalData: MissionPrimeAdditionalDataInput;
  craAirtableId: string;
};

export type BillingMonthlyPrimeLineValidationInput = PrimeLineValidationInputBase & {
  additionalData?: never;
  craAirtableId: null;
};

export type MonthlyPrimeLineValidationInput =
  | MissionMonthlyPrimeLineValidationInput
  | BillingMonthlyPrimeLineValidationInput;

/** Payload mutation `validateMonthlyPrime` (miroir contrat GQL TEC-2806). */
type MonthlyTamPrimeValidationAggregateField = Exclude<MonthlyTamPrimeAggregateField, 'regularisationReason'>;

export type MonthlyTamPrimeValidationInput = Pick<MonthlyTamPrime, MonthlyTamPrimeValidationAggregateField> & {
  primes: MonthlyPrimeLineValidationInput[];
};

export const isValidatedMonthlyTamPrime = (
  aggregate: MonthlyTamPrimeQueryResult,
): aggregate is ValidatedTamPrimeAggregate => aggregate.__typename === 'ValidatedTamPrime';

type MonthlyTamPrimeSummaryField =
  | 'bonus'
  | 'regularisation'
  | 'regularisationReason'
  | 'totalFreelanceMarge'
  | 'totalBillingMarge'
  | 'totalMarge'
  | 'totalPrime'
  | 'totalDeals'
  | 'totalMissions';

/** Prime validée dans l'agrégat annuel — inclut lignes (`GET_TAM_PRIMES`, TEC-2818). */
export type ValidatedTamPrimeWithLines = Pick<MonthlyTamPrime, MonthlyTamPrimeSummaryField> & {
  id: string;
  monthYear: string;
  validationDate: string;
  primes: TamPrimeLine[];
};

/** État route passé au clic loupe depuis le dashboard annuel (FR-004/FR-005). */
export type PrimesMonthlyNavigationState = {
  preloadedMonthlyTamPrime: MonthlyTamPrimeQueryResult;
};

export type MonthlyPrimeNavigationTarget = {
  pathname: string;
  search: string;
  /** Absent pour un mois « missing » (TEC-3300) : pas d'agrégat préchargé, l'écran mensuel recalcule à la volée. */
  state?: PrimesMonthlyNavigationState;
};

export type AnnualPrimeRowStatus = 'validated' | 'pending' | 'missing';

export type AnnualPrimeTableRow = Pick<MonthlyTamPrime, MonthlyTamPrimeSummaryField> & {
  rowId: string;
  monthYear: string;
  status: AnnualPrimeRowStatus;
};

export type AnnualTamPrimeAggregate = {
  tam: string;
  tamSeniority: TamSeniority;
  goalCompletion: number;
  validatedPrimes: ValidatedTamPrimeWithLines[];
  pendingPrimes: MonthlyTamPrimeAggregate | null;
};

export type GetTamPrimesQueryData = {
  getTamPrimes: AnnualTamPrimeAggregate | null;
};

/** TAM actif renvoyé par `GET_ACTIVE_TAMS` — sous-ensemble de {@link IBloomies}. */
export type ActiveTam = Required<Pick<IBloomies, 'email' | 'firstname' | 'lastname' | 'seniority' | 'luccaId'>> &
  Pick<IBloomies, 'staffEntryDate' | 'avatarUrl'>;

export type LifetimeMarginCumulAggregate = {
  tam: string;
  totalMargin: number;
  staffEntryDate: string | null;
};

export type GetLifetimeMarginCumulsQueryData = {
  getLifetimeMarginCumuls: LifetimeMarginCumulAggregate[];
};

export type CLevelMarginRankingMode = 'annual' | 'lifetime';

export type CLevelMarginRankingRow = {
  rank: number;
  tam: ActiveTam;
  tamInitials: string;
  amount: number;
  displayAmount: string;
  progressPercent: number;
};

/** Réponse Apollo `GET_ACTIVE_TAMS` (TEC-2815). */
export type GetActiveTamsQueryData = {
  getActiveTams: ActiveTam[];
};

/** Réponse Apollo `GET_MONTHLY_BLOOMIE_MARGINS` (PRO-1364) — voir type partagé `MonthlyBloomieMargin`. */
export type GetMonthlyBloomieMarginsQueryData = {
  getMonthlyBloomieMargins: MonthlyBloomieMargin[];
};

/** PRO-1364 — Ligne du tableau de marge Sales / TAD (marges du mois + identité + libellé affiché). */
export type SalesTadMarginRow = MonthlyBloomieMargin &
  Pick<SalesTadOption, 'firstname' | 'lastname' | 'luccaId'> & {
    displayName: string;
  };

export type MonthIndex = '01' | '02' | '03' | '04' | '05' | '06' | '07' | '08' | '09' | '10' | '11' | '12';

export type MonthlySynthesisCellStatus = 'loading' | 'error' | 'empty' | 'value';

export type MonthlySynthesisValueSource = 'validated' | 'pending';

export type MonthlySynthesisCell = {
  status: MonthlySynthesisCellStatus;
  totalMarge: number | null;
  totalPrime: number | null;
  /** Présent lorsque status === 'value' — pilote la couleur de cellule (TEC-3066). */
  valueSource: MonthlySynthesisValueSource | null;
};

export type CLevelSynthesisViewMode = 'marge' | 'prime';

export const CLEVEL_SYNTHESIS_EXPORT_FOOTER_ROW_ID = '__clevel-synthesis-export__' as const;

export type CLevelSynthesisExportFooterRow = {
  rowId: typeof CLEVEL_SYNTHESIS_EXPORT_FOOTER_ROW_ID;
  rowKind: 'export-footer';
};

export type CLevelMonthExportCellState = {
  monthIndex: MonthIndex;
  enabled: boolean;
};

export type CLevelPrimesDashboardGridRow = CLevelSynthesisRow | CLevelSynthesisExportFooterRow;

export type CLevelSynthesisRowStatus = 'loading' | 'success' | 'error';

/** Ligne pivot DataGrid synthèse C-Level (état UI). */
export type CLevelSynthesisRow = {
  rowId: string;
  tamEmail: string;
  tamDisplayName: string;
  tamFirstname: string;
  tamLastname: string;
  tamSeniority: TamSeniority | null;
  avatarUrl: string | null;
  luccaId: string | null;
  rowStatus: CLevelSynthesisRowStatus;
  months: Record<MonthIndex, MonthlySynthesisCell>;
  annualTamPrime?: AnnualTamPrimeAggregate;
};
